import {
  Bot,
  ClipboardList,
  Eye,
  FileText,
  HelpCircle,
  Link2,
  PhoneCall,
  Wrench,
} from 'lucide-react';

import { categoryMeta, originMeta, statusLabel } from './pendencies-adapter';

import type { PendencyWireStatus } from '../../lib/api-client';
import type { LifecyclePattern } from '../conversations/components/HybridPanel/LifecycleBadge';
import type { JSX } from 'react';

/** Wire status (7) → pattern visual canônico do `lc-badge` (ADR-119). */
const STATUS_PATTERN: Record<PendencyWireStatus, LifecyclePattern> = {
  open: 'initial',
  in_progress: 'in-progress',
  postponed: 'on-hold',
  resolved: 'completed-positive',
  cancelled: 'completed-negative',
  expired: 'completed-negative',
  archived: 'completed-negative',
};

/**
 * Badge de ciclo de vida da pendência — reusa o visual `lc-badge` (ADR-119),
 * label via `statusLabel` (Adiada N× quando postponeCount>0).
 */
export function PendencyLifecycleBadge({
  status,
  postponeCount = 0,
}: {
  status: PendencyWireStatus;
  postponeCount?: number;
}): JSX.Element {
  const pattern = STATUS_PATTERN[status];
  return (
    <span
      className={`lc-badge lc-badge--${pattern}`}
      data-testid={`pend-status-${status}`}
      aria-label={`Estado: ${statusLabel(status, postponeCount)}`}
    >
      {statusLabel(status, postponeCount)}
    </span>
  );
}

/** Rótulo curto da categoria (badge neutro DS) — degrada honesto. */
export function PendencyCatBadge({ category }: { category: string | null }): JSX.Element | null {
  if (category === null) return null;
  return <span className="badge badge--neutral">{categoryMeta(category).label}</span>;
}

/** Chip de origem (`source` free-form ADR-109) — degrada honesto. */
export function PendencyOriginBadge({ source }: { source: string }): JSX.Element {
  return <span className="origin-badge">{originMeta(source).label}</span>;
}

/** Variante visual do ícone de categoria (espelha o mockup). */
const CAT_ICON: Record<string, { icon: JSX.Element; cls: string }> = {
  customer_contact_required: { icon: <PhoneCall size={14} />, cls: '' },
  document_collection: { icon: <FileText size={14} />, cls: '' },
  external_action_required: { icon: <Link2 size={14} />, cls: '' },
  internal_review: { icon: <Eye size={14} />, cls: 'pendency-card__cat-icon--review' },
  technical_escalation: { icon: <Wrench size={14} />, cls: 'pendency-card__cat-icon--escalation' },
  commitment_fulfillment_confirmation: {
    icon: <ClipboardList size={14} />,
    cls: 'pendency-card__cat-icon--commitment',
  },
  ai_suggestion_review: { icon: <Bot size={14} />, cls: 'pendency-card__cat-icon--ai' },
};

/** Ícone da categoria — fallback honesto (HelpCircle) p/ categoria desconhecida/nula. */
export function PendencyCatIcon({ category }: { category: string | null }): JSX.Element {
  const meta = category !== null ? CAT_ICON[category] : undefined;
  const icon = meta?.icon ?? <HelpCircle size={14} />;
  const cls = meta?.cls ?? '';
  return <div className={`pendency-card__cat-icon ${cls}`.trim()}>{icon}</div>;
}
